import React, { useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Import the plugin for jsPDF
import IconButton from '../../elements/IconButton';
import Button from '../../elements/Button';
import { Sector } from 'recharts';

const Main = (props) => {
    const [loadedData, setLoadedData] = useState([]);
    const [stillGettingData, setStillGettingData] = useState(0);
    const isCancelled = useRef(false); // Used to track if fetching was cancelled

    const handleCancelFetch = () => {
        isCancelled.current = true; // Set flag to true when fetching is cancelled
    };

    const fetchAllData = (exportType, selCol) => {
        let skip = 0;
        const limit = 15;
        let allData = [];
        setStillGettingData(1);

        const fetchNext = () => {
            if (isCancelled.current) {
                console.log("Fetching has been cancelled by the user.");
                setStillGettingData(0); // Update state to reflect cancellation
                return;
            }
            getData(skip, limit, (hasMoreData, newData) => {
                if (false) {
                    allData = allData.concat(newData);
                    skip += limit;
                    fetchNext();
                } else {
                    allData = allData.concat(newData);
                    console.log(allData)
                    if (exportType === 'CSV') {
                        downloadCSV(allData, props.exportKeys, props.fileTitle, selCol);
                    } else if (exportType === 'PDF') {
                        downloadPDF(allData, props.exportKeys, props.fileTitle, selCol);
                    }
                    setStillGettingData(2);
                }
            });
        };

        fetchNext();
    };

    const getData = (skip, limit, onCompletion) => {
        var myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + localStorage.getItem('access_token'));
        myHeaders.append("Content-Type", "application/json");

        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            redirect: 'follow',
            body: JSON.stringify(props.filterData)
        };

        props.tokenSafeAPIRequest(
            `${props.url}${props.specific ? '/' + props.specific : ''}?limit=${limit}&fetch_links=true&skip=${skip}&sort_field=${props.sort}&start_date=${props.calStartDate}&end_date=${props.calEndDate}&active_only=false`,
            requestOptions,
            '',
            (result, status) => {
                if ('postProcessing' in props) {
                    result = props.postProcessing(result);
                }

                console.log(result)

                if ('reports' in result) {
                    processResult(result.reports, onCompletion);
                } else if ('accounts' in result) {
                    processResult(result.accounts, onCompletion);
                } else if ('transactions' in result) {
                    processResult(result.transactions, onCompletion);
                } else if ('dealerships' in result) {
                    processResult(result.dealerships, onCompletion);
                } else if ('spiffs' in result) {
                    processResult(result.spiffs, onCompletion);
                } else if ('dppps_and_gap' in result) {
                    processResult(result.dppps_and_gap, onCompletion);
                } else if ('rows' in result) {
                    processResult(result, onCompletion);
                } else if ('agents' in result) {
                    processResult(result, onCompletion);
                } else {
                    processResult(result, onCompletion);
                }
            }
        );
    };

    const processResult = (data, onCompletion) => {
        console.log(data)
        if (data.rows && data.rows.length > 0) {
            setLoadedData((loadedData) => [...loadedData, ...data.rows]);
            onCompletion(true, data);
        } else {
            onCompletion(false);
        }
    };

    const downloadCSV = (data, keys, filename = 'data.csv', selCol) => {
        console.log(keys)
        console.log(data)

        if (!selCol || !Array.isArray(selCol)) selCol = keys.map(() => true);

        // Flatten helper (same as PDF)
        const flattenObject = (obj, parentKey = '', res = {}) => {
            for (let key in obj) {
                let propName = parentKey ? parentKey + '.' + key : key;
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    flattenObject(obj[key], propName, res);
                } else {
                    res[propName] = obj[key];
                }
            }
            return res;
        };

        const flattenedData = (data && data[0] && data[0].rows) ? data[0].rows.map(item => flattenObject(item)) : [];

        const formatMoney = (value) => {
            const num = Number(value);
            if (!Number.isFinite(num)) return "$0.00";
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(num);
        };

        const formatNumber = (value) => {
            const num = Number(value);
            if (!Number.isFinite(num)) return '0.00';
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(num);
        };

        // Totals
        let totalNet = 0;
        let totalCC = 0;
        let totalMonthlyCC = 0;
        let totalCheck = 0;

        flattenedData.forEach(item => {
            totalNet += Number(item.net) || 0;
            totalCC += Number(item.contract_count) || 0;
            totalMonthlyCC += Number(item.monthly_cc) || 0;
            totalCheck += Number(item.check) || 0;
        });

        // Prepare rows following PDF formatting rules
        const rows = flattenedData.map(item =>
            keys.map((key, index) => {
                const raw = item[key.id];
                const value = (raw !== undefined && raw !== null) ? raw : "";
                if (index === 2 || index === 3) return formatNumber(value);
                if (index === 4 || index === 5) return formatMoney(value);
                return `${value}`;
            })
        );

        // Filter columns according to selCol
        const filteredHeaders = keys.map(k => k.label).filter((_, i) => selCol[i]);
        const filteredRows = rows.map(r => r.filter((_, i) => selCol[i]));

        const insideSales = (data && data[0] && data[0].inside_sales_net) ? data[0].inside_sales_net : 0;

        // Build CSV content with proper quoting for filtered table
        let csvContent = filteredHeaders.join(',') + '\n';
        filteredRows.forEach(row => {
            const line = row.map(cell => {
                const cellStr = cell === null || cell === undefined ? '' : String(cell);
                return '"' + cellStr.replace(/"/g, '""') + '"';
            }).join(',');
            csvContent += line + '\n';
        });

        // Now append full-header summary rows (not affected by selCol)
        csvContent += '\n';
        const fullHeaders = keys.map(k => k.label).join(',') + '\n';
        csvContent += fullHeaders;
        const insideSalesFull = ['', 'Inside Sales', '', '', formatMoney(insideSales), ''];
        const totalFull = ['', 'Total', formatNumber(totalCC), formatNumber(totalMonthlyCC), formatMoney(totalNet + Number(insideSales)), formatNumber(totalCheck)];
        [insideSalesFull, totalFull].forEach(row => {
            const line = row.map(cell => '"' + (cell === null || cell === undefined ? '' : String(cell)).replace(/"/g, '""') + '"').join(',');
            csvContent += line + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };
    

    const downloadPDF = (data, keys, filename = 'data.pdf', selCol) => {
        const doc = new jsPDF({orientation: 'landscape'});

        let totalInsideSales = data[0].inside_sales_net;
        data = data[0].rows;
        console.log(data)
        console.log(keys)
    
        // Add title
        doc.text(props.fileTitle || 'Report', 20, 10);

        let pageCount = 1;

        const addPageNumbers = (doc) => {
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height;
            const pageWidth = pageSize.width;
    
            // Add footer with page number
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.text(
                    `Page ${i}`,
                    pageWidth - 30,
                    pageHeight - 10
                );
            }
        };

        const formatMoney = (value) => {
            const num = Number(value);
            if (!Number.isFinite(num)) return "$0.00";

            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(num);
        };
    
        // Helper function to flatten nested objects (same as used in CSV)
        const flattenObject = (obj, parentKey = '', res = {}) => {
            for (let key in obj) {
                let propName = parentKey ? parentKey + '.' + key : key;
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    flattenObject(obj[key], propName, res);
                } else {
                    res[propName] = obj[key];
                }
            }
            return res;
        };
    
        // Flatten the data before adding to the table
        const flattenedData = data.map(item => flattenObject(item));

        console.log(flattenedData)

        function formatNumber(value) {
            try {
                // Attempt to convert the input to a number
                const num = parseFloat(value);
                // Check if the conversion resulted in a valid number
                if (!isNaN(num)) {
                // Use Intl.NumberFormat for US-style number formatting with two decimal places
                return new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(num);
                } else {
                // Return a default value if the input is not a valid number
                return '0.00';
                }
            } catch (e) {
                // In case of any unexpected errors, return a default value
                return '0.00';
            }
        }
    
        // Prepare table data for PDF (headers and rows)
        let totalNet = 0;
        let totalCC = 0;
        let totalMonthlyCC = 0;
        let totalCheck = 0;

        flattenedData.map((item, index) => {
            totalNet = totalNet + item.net;
            totalCC = totalCC + item.contract_count;
            totalMonthlyCC = totalMonthlyCC + item.monthly_cc;
            totalCheck = totalCheck + item.check;
        })

        const rows = flattenedData.map((item, index) =>
            keys.map((key, index) => {
            const value = item[key.id] || "";
            if (index === 2 || index === 3) return formatNumber(value);
            if (index === 4 || index === 5) return formatMoney(value)
            return value
            })
        );

        let headers = keys.map(key => key.label);

        console.log(headers)
        console.log(rows)
        console.log(selCol)

        const result = rows.map(row =>
            row.filter((_, index) => selCol[index])
        );

        const filteredHeaders = headers.filter((_, index) => selCol[index])

        // Add the filtered table using autoTable
        doc.autoTable({
            head: [filteredHeaders],
            body: result,
        });

        // Add a second table containing the summary rows with full (unfiltered) columns
        const fullHeaders = headers;
        const insideSalesFull = ['', 'Inside Sales', '', '', formatMoney(totalInsideSales), ''];
        const totalFull = ['', 'Total', formatNumber(totalCC), formatNumber(totalMonthlyCC), formatMoney(totalNet + totalInsideSales), formatNumber(totalCheck)];

        const startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : undefined;
        doc.autoTable({
            head: [fullHeaders],
            body: [insideSalesFull, totalFull],
            startY,
        });

        addPageNumbers(doc);
    
        // Save the PDF
        doc.save(filename);
    };

    const [selCol, setSelCol] = useState([true, true, true, true, true, true])

    const handleColChanges = (col) => {
        setSelCol((prev) =>
            prev.map((v, i) => (i === col ? !v : v))
        )
    }
    
    return (
        <div style={{ width: '360px', height: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexBasis: '10%', display: 'flex', flexDirection: 'row' }}>
                <span style={{ fontWeight: '500', fontSize: '20px', flexBasis: '99%' }}>Export Report</span>
                <IconButton img={false} colors={props.colors} Style={{ borderRadius: '3px', backgroundColor: props.colors.border, width: '24px' }} size="16px">
                    <div onClick={() => { handleCancelFetch(); props.setIsOpen() }} style={{ paddingLeft: '3px', paddingRight: '3px' }}><span onClick={() => { handleCancelFetch(); props.setIsOpen() }}>x</span></div>
                </IconButton>
            </div>
            <div style={{ flexBasis: '90%', display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <span style={{fontWeight: "700"}}>Included Columns:</span>
                    <div onClick={() => handleColChanges(0)} style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '5px'}}>
                        <div style={{cursor: 'pointer', width: '20px', height: '20px', border: selCol[0] ? '2px solid ' + props.colors.primary:'2px solid ' + props.colors.border, borderRadius: '5px', backgroundColor: selCol[0] ? props.colors.primary:''}}></div>
                        <span style={{marginLeft: "10px", fontWeight: '500'}}>Code</span>
                    </div>
                    <div onClick={() => handleColChanges(1)} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <div style={{cursor: 'pointer', width: '20px', height: '20px', border: selCol[1] ? '2px solid ' + props.colors.primary:'2px solid ' + props.colors.border, borderRadius: '5px', backgroundColor: selCol[1] ? props.colors.primary:''}}></div>
                        <span style={{marginLeft: "10px", fontWeight: '500'}}>Name</span>
                    </div>
                    <div onClick={() => handleColChanges(2)} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <div style={{cursor: 'pointer', width: '20px', height: '20px', border: selCol[2] ? '2px solid ' + props.colors.primary:'2px solid ' + props.colors.border, borderRadius: '5px', backgroundColor: selCol[2] ? props.colors.primary:''}}></div>
                        <span style={{marginLeft: "10px", fontWeight: '500'}}>CC</span>
                    </div>
                    <div onClick={() => handleColChanges(3)} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <div style={{cursor: 'pointer', width: '20px', height: '20px', border: selCol[3] ? '2px solid ' + props.colors.primary:'2px solid ' + props.colors.border, borderRadius: '5px', backgroundColor: selCol[3] ? props.colors.primary:''}}></div>
                        <span style={{marginLeft: "10px", fontWeight: '500'}}>Monthly CC</span>
                    </div>
                    <div onClick={() => handleColChanges(4)} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <div style={{cursor: 'pointer', width: '20px', height: '20px', border: selCol[4] ? '2px solid ' + props.colors.primary:'2px solid ' + props.colors.border, borderRadius: '5px', backgroundColor: selCol[4] ? props.colors.primary:''}}></div>
                        <span style={{marginLeft: "10px", fontWeight: '500'}}>Net</span>
                    </div>
                    <div onClick={() => handleColChanges(5)} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <div style={{cursor: 'pointer', width: '20px', height: '20px', border: selCol[5] ? '2px solid ' + props.colors.primary:'2px solid ' + props.colors.border, borderRadius: '5px', backgroundColor: selCol[5] ? props.colors.primary:''}}></div>
                        <span style={{marginLeft: "10px", fontWeight: '500'}}>Check</span>
                    </div>
                </div>
                <span style={{ fontWeight: '500', fontSize: '16px', marginTop: '15px' }}>Downloaded Rows: {loadedData.length}</span>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('CSV', selCol)}>Download CSV</Button>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('PDF', selCol)}>Download PDF</Button>
            </div>
        </div>
    );
};

export default Main;
